import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

import * as api from 'api';
import localStorage from '../localStorageUtils';

import { AppDispatch, AppState } from 'store';
import { ClimberCategory, RouteCategory, ROUTE_CATEGORIES } from '../constants';
import { setError } from './general';

const MAX_CLIMBERS_PER_ROUTE = 100;

export interface ScoringState {
  allClimbers: Climber[];
  routeCount?: Record<RouteCategory, number>;
  routesPerCategory?: Record<RouteCategory, Record<ClimberCategory, number[]>>;
  selectedClimbers: SelectedClimbers;
  scores: {
    lead: {
      [route: number]: {
        [climberId: string]: LeadRouteScore;
      };
    };
    boulder: {
      [route: number]: {
        [climberId: string]: BoulderRouteScore;
      };
    };
  };
  selectedRouteCategory: RouteCategory;
  selectedRoutes: SelectedRoutes;
}

const initialState: ScoringState = {
  allClimbers: [],
  routeCount: undefined,
  routesPerCategory: undefined,
  selectedClimbers: localStorage.selectedClimbers || {},
  scores: {
    boulder: {},
    lead: {},
  },
  selectedRouteCategory:
    localStorage.selectedRouteCategory || ROUTE_CATEGORIES[0],
  selectedRoutes: localStorage.selectedRoutes || {},
};

export const scoringSlice = createSlice({
  name: 'scoring',
  initialState,
  reducers: {
    setAllClimbers: (state, { payload }: PayloadAction<Climber[]>) => {
      state.allClimbers = payload;
    },
    setSelectedClimbers: (
      state,
      { payload }: PayloadAction<SelectedClimbers>,
    ) => {
      state.selectedClimbers = payload;
    },
    setSelectedRoutes: (state, { payload }: PayloadAction<SelectedRoutes>) => {
      state.selectedRoutes = payload;
    },
    setAllRoutes: (
      state,
      { payload }: PayloadAction<Record<RouteCategory, number>>,
    ) => {
      state.routeCount = payload;
    },
    setRoutesPerCategory: (
      state,
      {
        payload,
      }: PayloadAction<
        Record<RouteCategory, Record<ClimberCategory, number[]>>
      >,
    ) => {
      state.routesPerCategory = payload;
    },
    setScore: (
      state,
      {
        payload: { route, score, routeCategory, climberId },
      }: PayloadAction<{
        route: number;
        score: RouteScore;
        routeCategory: RouteCategory;
        climberId: string;
      }>,
    ) => {
      if (!state.scores[routeCategory][route]) {
        state.scores[routeCategory][route] = {
          [climberId]: score as RouteCategory extends 'lead'
            ? LeadRouteScore
            : BoulderRouteScore,
        };
      } else {
        state.scores[routeCategory][route]![climberId] = score;
      }
    },
    selectRoute: (
      state,
      {
        payload: { route, routeIndex },
      }: PayloadAction<{ route: number | null; routeIndex: number }>,
    ) => {
      const selectedCategory = state.selectedRouteCategory;
      if (route && route > state.routeCount?.[selectedCategory]!) {
        console.warn('selected climbing route does not exist in category', {
          route,
          selectedCategory,
        });
        return;
      }

      if (!state.selectedRoutes[selectedCategory]) {
        state.selectedRoutes[selectedCategory] = [];
      }

      state.selectedRoutes[selectedCategory]![routeIndex] = route;

      localStorage.selectedRoutes = state.selectedRoutes;
    },
    removeSelectedRoute(state, { payload: routeIndex }: PayloadAction<number>) {
      const selectedCategory = state.selectedRouteCategory;
      if (!state.selectedRoutes[selectedCategory]) {
        return;
      }

      state.selectedRoutes[selectedCategory]!.splice(routeIndex, 1);

      localStorage.selectedRoutes = state.selectedRoutes;
    },
    addClimberToRoute: (
      state,
      {
        payload: { climberId, route },
      }: PayloadAction<{ climberId: string; route: number }>,
    ) => {
      const selectedCategory = state.selectedRouteCategory;

      if (!state.selectedClimbers[selectedCategory]) {
        state.selectedClimbers[selectedCategory] = {};
      }

      const selectedClimbers = state.selectedClimbers[selectedCategory]!;

      if (!selectedClimbers[route]) {
        selectedClimbers[route] = [climberId];
      } else {
        if (selectedClimbers[route]!.includes(climberId)) {
          selectedClimbers[route] = selectedClimbers[route]!.filter(
            (c) => c !== climberId,
          );
        }

        selectedClimbers[route]!.unshift(climberId);

        selectedClimbers[route]! = selectedClimbers[route]!.slice(
          0,
          MAX_CLIMBERS_PER_ROUTE,
        );
      }

      localStorage.selectedClimbers = state.selectedClimbers;
    },
    selectRouteCategory: (
      state,
      { payload: category }: PayloadAction<RouteCategory>,
    ) => {
      state.selectedRouteCategory = category;
      localStorage.selectedRouteCategory = state.selectedRouteCategory;

      if (!state.selectedRoutes[category]) {
        state.selectedRoutes[category] = [];
        localStorage.selectedRoutes = state.selectedRoutes;
      }
    },

    increaseSelectedRoutes: (state) => {
      const selectedCategory = state.selectedRouteCategory;

      state.selectedRoutes[selectedCategory]!.push(null);
      localStorage.selectedRoutes = state.selectedRoutes;
    },
    decreaseSelectedRoutes: (state) => {
      const selectedCategory = state.selectedRouteCategory;

      state.selectedRoutes[selectedCategory]!.pop();
      localStorage.selectedRoutes = state.selectedRoutes;
    },
  },
});

export const {
  setAllClimbers,
  setAllRoutes,
  setScore,
  selectRoute,
  removeSelectedRoute,
  setRoutesPerCategory,
  selectRouteCategory,
  increaseSelectedRoutes,
  decreaseSelectedRoutes,
} = scoringSlice.actions;

export const allRoutesSelector = createSelector(
  (state: AppState) => state.scoring.routeCount,
  (state: AppState) => state.scoring.selectedRouteCategory,
  (routeCounts, selectedCategory) => {
    const routeCount = routeCounts?.[selectedCategory] || 0;
    return Array.from({ length: routeCount }, (_, i) => i + 1);
  },
);

export function initializeScoring() {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    try {
      const { climbers, routeCount, routesPerCategory } =
        await api.fetchOverviewData();

      dispatch(setAllClimbers(climbers));
      dispatch(setAllRoutes(routeCount));
      dispatch(setRoutesPerCategory(routesPerCategory));

      const { selectedRoutes, selectedClimbers } = getState().scoring;

      if (!climbers.length || !Object.keys(routeCount).length) {
        return;
      }

      // Filter out removed routes
      dispatch(
        scoringSlice.actions.setSelectedRoutes(
          Object.entries(selectedRoutes).reduce(
            (acc, [routeCategory, routes]) => {
              acc[routeCategory] = routes.filter(
                (route) =>
                  route === null ||
                  route <= routeCount[routeCategory as RouteCategory],
              );

              return acc;
            },
            {} as SelectedRoutes,
          ),
        ),
      );

      // Filter out selected climbers - remove lost climbers
      dispatch(
        scoringSlice.actions.setSelectedClimbers(
          Object.entries(selectedClimbers).reduce(
            (acc, [routeCategory, routeData]) => {
              acc[routeCategory] = Object.entries(routeData).reduce(
                (routeData, [route, routeClimbers]) => {
                  routeData[route as unknown as number] = routeClimbers.filter(
                    (climberId) => climbers.find((c) => c.id === climberId),
                  );
                  return routeData;
                },
                {} as Record<number, string[]>,
              );
              return acc;
            },
            {} as SelectedClimbers,
          ),
        ),
      );
    } catch (err) {
      console.error('Failed to fetch grading data', err);
      dispatch(
        setError('Failed to fetch grading data. Please refresh the page'),
      );
    }
  };
}

export function addClimberToRoute({
  climberId,
  route,
}: {
  climberId: string;
  route: number;
}) {
  return (dispatch: AppDispatch) => {
    dispatch(scoringSlice.actions.addClimberToRoute({ climberId, route }));
  };
}

export function fetchScore({
  climberId,
  route,
  routeCategory,
}: {
  climberId: string;
  route: number;
  routeCategory: RouteCategory;
}) {
  return async (dispatch: AppDispatch) => {
    try {
      const score = await api.fetchScore({ climberId, route, routeCategory });

      dispatch(setScore({ route, score, routeCategory, climberId }));
    } catch (err) {
      console.error('Failed to fetch score', err);
      dispatch(setError('Failed to fetch score'));
    }
  };
}

export function updateScore({
  score,
  routeCategory,
}: {
  score: RouteScore;
  routeCategory: RouteCategory;
}) {
  return async (dispatch: AppDispatch) => {
    try {
      const updatedScore = await api.updateScore({ score, routeCategory });

      dispatch(
        setScore({
          route: updatedScore.route,
          score: updatedScore,
          routeCategory,
          climberId: updatedScore.climberId,
        }),
      );
    } catch (err) {
      console.error('Failed to update score', err);
      dispatch(setError('Failed to update score'));
    }
  };
}

export default scoringSlice.reducer;
